/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spin,
} from 'antd';
import {
  deleteAcreedorDatoDePago,
  getAcreedorDatoDePago,
  getAcreedoresDatosDePago,
  patchAcreedorDatoDePago,
  postAcreedorDatoDePago,
} from 'api/configuraciones/acreedoresDatosDePago';
import { formatReceived } from 'utils/formatValues';
import { getEstadosGlobales } from 'api/catalogos';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import { getMetodosDePagos } from 'api/catalogos/metodosDePagos';
import { getBancos } from 'api/configuraciones';
import { getModelPermission } from 'utils/permission';
import ModalDelete from 'components/ModalDelete';

export const acreedoresDatosDePagoPermission = {
  permissionModel: 'acreedordatodepago',
  catalogsModels: [],
  tabsModels: [],
};

const AcreedoresDatosDePago = ({
  acreedorId,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estadosGlobales, setestadosGlobales] = useState([]);
  const [metodosDePago, setMetodosDePago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [data, setData] = useState([]);
  const [disableFields, setDisableFields] = useState(true);

  const getPermission = getModelPermission(
    acreedoresDatosDePagoPermission.permissionModel,
    acreedoresDatosDePagoPermission.catalogsModels,
    acreedoresDatosDePagoPermission.tabsModels,
    false,
  );

  const fetchData = async () => {
    setLoading(true);
    const _data = await getAcreedoresDatosDePago({ acreedor: acreedorId });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _metodosDePago,
        _bancos,
        _estadosGlobales,
        _data,
      ] = await Promise.all([
        getMetodosDePagos(),
        getBancos(),
        getEstadosGlobales(),
        getAcreedoresDatosDePago({ acreedor: acreedorId }),
      ]);
      if (mounted) {
        setMetodosDePago(_metodosDePago);
        setBancos(_bancos);
        setestadosGlobales(_estadosGlobales);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableFields(true);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(false);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _acreedorDatoDePago = await getAcreedorDatoDePago(match.id);
    const _metodoDePago = metodosDePago.find((e) => e.id === _acreedorDatoDePago?.metodo_de_pago);
    // eslint-disable-next-line no-unused-expressions
    _metodoDePago.requiere_datos_bancarios ? setDisableFields(false) : setDisableFields(true);
    setTimeout(() => {
      if (_acreedorDatoDePago) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_acreedorDatoDePago));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      // eslint-disable-next-line no-unused-expressions
      values.banco === undefined ? values.banco = null : values.banco;
      // eslint-disable-next-line no-unused-expressions, max-len
      values.numero_de_cuenta === undefined ? values.numero_de_cuenta = null : values.numero_de_cuenta;
      // eslint-disable-next-line no-unused-expressions
      values.clabe === undefined ? values.clabe = null : values.clabe;
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchAcreedorDatoDePago(key, { ...values, acreedor: acreedorId }) : postAcreedorDatoDePago({ ...values, acreedor: acreedorId }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteAcreedorDatoDePago(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onChangeMetodoDePago = (val) => {
    form.resetFields(['banco', 'numero_de_cuenta', 'clabe']);
    if (val) {
      const match = metodosDePago.find((e) => e.id === val)?.requiere_datos_bancarios;
      if (match) {
        setDisableFields(false);
      } else {
        setDisableFields(true);
      }
    }
  };

  const columns = [
    {
      titleText: 'Forma De Pago',
      dataIndex: 'metodo_de_pago',
      key: 'metodo_de_pago',
      width: 200,
      render: (val) => metodosDePago.find((e) => e.id === val)?.concepto,
    },
    {
      titleText: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
      width: 200,
      render: (val) => bancos.find((e) => e.id === val)?.nombre,
    },
    {
      titleText: 'Cuenta',
      dataIndex: 'numero_de_cuenta',
      key: 'numero_de_cuenta',
      width: 200,
    },
    {
      titleText: 'Clabe',
      dataIndex: 'clabe',
      key: 'clabe',
      width: 200,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={getPermission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Dato de Pago`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          width="50%"
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="metodo_de_pago"
                  label="Forma de Pago"
                  rules={rules.required}
                >
                  <Select
                    keyProp="id"
                    labelProp="concepto"
                    dataSource={metodosDePago}
                    onChange={onChangeMetodoDePago}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="banco"
                  label="Banco"
                  rules={!disableFields && rules.required}
                >
                  <Select
                    keyProp="id"
                    labelProp="nombre"
                    dataSource={bancos}
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="numero_de_cuenta"
                  label="No. de Cuenta"
                  rules={!disableFields && rules.required}
                  disabled={disableFields}
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <NumericInput
                  name="clabe"
                  label="CLABE"
                  rules={!disableFields && rules.required}
                  disabled={disableFields}
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estado"
                  initialValue={1}
                >
                  <Select
                    dataSource={estadosGlobales}
                    disabled={selectedRowKeys.length <= 0}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="El Dato de Pago"
        />
        {/* <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="El Dato de Pago"
        /> */}
      </Spin>
    </Row>
  );
};

export default AcreedoresDatosDePago;
